import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { Subject, takeUntil } from 'rxjs';
import { NotificationService } from '../../../services/notification.service';
import { Notification } from '../../../models/notification.model';

@Component({
  selector: 'app-notification-dropdown',
  standalone: true,
  imports: [CommonModule, ButtonModule, MenuModule, OverlayBadgeModule],
  templateUrl: './notification-dropdown.component.html',
  styleUrl: './notification-dropdown.component.scss'
})
export class NotificationDropdownComponent implements OnInit, OnDestroy {
  notifications: Notification[] = [];
  unreadCount: number = 0;
  isDropdownOpen: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    private notificationService: NotificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.notificationService.notifications$
      .pipe(takeUntil(this.destroy$))
      .subscribe(notifications => {
        this.notifications = notifications;
      });

    this.notificationService.unreadCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.unreadCount = count;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.notification-dropdown')) {
      this.closeDropdown();
    }
  }

  getNotificationIcon(type: string): string {
    switch (type) {
      case 'success':
        return 'pi pi-check-circle';
      case 'warning':
        return 'pi pi-exclamation-triangle';
      case 'error':
        return 'pi pi-times-circle';
      case 'info':
        return 'pi pi-info-circle';
      case 'system':
        return 'pi pi-star';
      default:
        return 'pi pi-bell';
    }
  }

  getNotificationColor(type: string): string {
    switch (type) {
      case 'success':
        return 'var(--color-brand-success)'; // Green for success
      case 'warning':
        return 'var(--color-brand-warning)'; // Orange for warning
      case 'error':
        return 'var(--color-brand-error)'; // Red for error
      case 'info':
        return 'var(--color-brand-info)'; // Blue for info
      case 'system':
        return 'var(--color-accent-blue)'; // Navy blue for system/approved
      default:
        return 'var(--color-text-primary)'; // Default text color
    }
  }

  formatTimestamp(timestamp: Date): string {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    return timestamp.toLocaleDateString();
  }

  onNotificationClick(notification: Notification): void {
    // Mark as read and handle API response
    this.notificationService.markAsRead(notification.id).subscribe();
    
    // Navigate if action URL is provided
    if (notification.actionUrl) {
      this.router.navigate([notification.actionUrl]);
    }
  }

  markAllAsRead(): void {
    this.notificationService.markAllAsRead().subscribe();
  }

  clearAllNotifications(): void {
    this.notificationService.clearAllNotifications().subscribe();
  }

  hasNotifications(): boolean {
    return this.notifications.length > 0;
  }

  hasUnreadNotifications(): boolean {
    return this.unreadCount > 0;
  }

  refreshNotifications(): void {
    this.notificationService.refreshNotifications().subscribe();
  }

  formatMessage(message: string): string {
    // Bold the user name in the message (assuming it's at the start)
    const words = message.split(' ');
    if (words.length > 1) {
      const userName = words[0];
      const restOfMessage = words.slice(1).join(' ');
      return `<strong>${userName}</strong> ${restOfMessage}`;
    }
    return message;
  }
}
